import { Box, Skeleton } from "@mui/material";
import { logger } from "@platform/browser-logger";
import { CellReviewProvider } from "@platform/btx-plan-designer";
import { AiFieldLookupProvider } from "@platform/plan-grid-core";
import {
  DialogProvider,
  useMpxAgGridState,
  useNotification,
} from "@platform/hooks";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import { Alert } from "@platform/design-system";

import {
  useDeleteProposalCommission,
  useMaterializeCompsCommission,
  useReloadCompsTableAfterWrites,
  useUpdateCompsColumnFreeText,
  useUpdateProposalCommission,
  useUpdateProposalFreeText,
} from "../../../../../../api/comps/mutations";
import { useRfpDetails } from "../../../../../../hooks/useRfpDetails";
import { extractErrorMessage } from "../../../../../../utils";
import { useCarrierSequenceContext } from "../../CarrierSequenceContext";
import { CellReviewCommentDialogRenderer } from "../shared/cellReviews/CellReviewCommentDialogRenderer";
import { useCellReviewContextMenu } from "../shared/cellReviews/useCellReviewContextMenu";

import { deriveCompsCellForContextMenu } from "./compsCellReviewIdentity";
import { CompsGrid } from "./CompsGrid";
import { RowVisibilityProvider } from "./rowVisibility";
import { INCUMBENT_COL_KEY, REQUESTED_COL_KEY } from "./types";
import { useCompsAiFieldLookup } from "./useCompsAiFieldLookup";
import { useCompsCellReviews } from "./useCompsCellReviews";
import { useCompsTable } from "./useCompsTable";
import { useCompsVisibilityGridState } from "./useCompsVisibilityGridState";

import type { CompsTableHeapedAmount } from "@platform/api";
import type { MpxAgGridStateProps } from "@platform/hooks";
import type { CellValueChangedEvent } from "ag-grid-community";
import type {
  CompsColumnKind,
  UpdateProposalCommissionPayload,
} from "../../../../../../api/comps/client";
import type { CarrierCommissionCell, CompsGridRow } from "./types";

const COMPS_GRID_ID = "rfp-comps";

// ---------------------------------------------------------------------------
// Inner component — must live inside CellReviewProvider + DialogProvider
// ---------------------------------------------------------------------------

interface CompsGridContentProps {
  rowData: CompsGridRow[];
  hiddenRowGroupIds: Set<string>;
  colDefs: ReturnType<typeof useCompsTable>["colDefs"];
  gridStateProps: ReturnType<typeof useMpxAgGridState>["gridProps"];
  context: ReturnType<typeof useCompsTable>["context"];
  onCellValueChanged: (event: CellValueChangedEvent<CompsGridRow>) => void;
}

const CompsGridContent = ({
  rowData,
  hiddenRowGroupIds,
  colDefs,
  gridStateProps,
  context,
  onCellValueChanged,
}: CompsGridContentProps): React.JSX.Element => {
  const getContextMenuItems = useCellReviewContextMenu(
    deriveCompsCellForContextMenu,
  );
  return (
    <CompsGrid
      rowData={rowData}
      hiddenRowGroupIds={hiddenRowGroupIds}
      colDefs={colDefs}
      gridStateProps={gridStateProps}
      context={context}
      onCellValueChanged={onCellValueChanged}
      getContextMenuItems={getContextMenuItems}
    />
  );
};

// ---------------------------------------------------------------------------
// Helpers (no component closure needed)
// ---------------------------------------------------------------------------

const buildCommissionPayload = (
  cell: CarrierCommissionCell,
): UpdateProposalCommissionPayload => {
  const { amount } = cell;
  const isHeaped = amount !== null && typeof amount === "object";
  return {
    returnedType: cell.commissionType ?? "",
    returnedAmount: typeof amount === "number" ? amount : null,
    returnedHeapedAmount: isHeaped
      ? [
          (amount as CompsTableHeapedAmount).first,
          (amount as CompsTableHeapedAmount).thereafter,
        ]
      : null,
  };
};

const collectFreeTextValues = (
  rows: CompsGridRow[],
  carrierId: string,
  fieldByRowId: Map<string, string>,
): Record<string, string> =>
  Object.fromEntries(
    rows.flatMap((row) => {
      const field = fieldByRowId.get(row.rowId);
      if (!field) return [];
      const value = row[carrierId];
      return [[field, typeof value === "string" ? value : ""]];
    }),
  );

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Comps = (): React.JSX.Element => {
  const { id: rfpId } = useParams<{ id: string }>();

  const {
    data: rfpDetails,
    isLoading: isLoadingDetails,
    error: rfpDetailsError,
  } = useRfpDetails({ rfpId: rfpId ?? "", enabled: !!rfpId });

  const ecoRfpId = rfpDetails?.ecoRfpId ?? "";

  // Carrier order the analyst applied to all SBS pages from Quotes (CD-1806).
  const { appliedToAllCarrierOrder } = useCarrierSequenceContext();

  const aiFieldLookup = useCompsAiFieldLookup(ecoRfpId);

  const {
    rowData,
    colDefs,
    context,
    isLoading: isLoadingComps,
    errorMessage,
  } = useCompsTable({ ecoRfpId, carrierOrder: appliedToAllCarrierOrder });
  useReloadCompsTableAfterWrites(ecoRfpId);

  const { gridProps: rawMpxGridStateProps, isLoading: isLoadingGridState } =
    useMpxAgGridState({
      scopeType: "rfp",
      scopeId: ecoRfpId,
      gridId: COMPS_GRID_ID,
      enabled: !!ecoRfpId,
    });

  // Column order here is fixed by `buildCompsColDefs` (Coverage, Incumbent,
  // Requested, then carriers) and never user-adjustable — columns are
  // non-movable. Drop any persisted `columnOrder` so a stale saved state
  // can't override that order on mount and then have AG Grid snap back to
  // the canonical order on the next `columnDefs` update (e.g. after an
  // edit's refetch), which reads as columns reshuffling mid-session.
  const mpxGridStateProps: MpxAgGridStateProps = useMemo(
    () => ({
      ...rawMpxGridStateProps,
      initialState: rawMpxGridStateProps.initialState
        ? { ...rawMpxGridStateProps.initialState, columnOrder: undefined }
        : undefined,
    }),
    [rawMpxGridStateProps],
  );

  // Hidden rows and columns are shared per RFP rather than per user, so both
  // halves come from the comps-visibility store: columns through the grid's
  // own state, rows as a Set the grid's external filter reads.
  const {
    gridProps: gridStateProps,
    hiddenRowGroupIds,
    toggleRowGroup,
    isLoading: isLoadingVisibility,
  } = useCompsVisibilityGridState({
    rfpId: ecoRfpId,
    rowData,
    gridProps: mpxGridStateProps,
    gridStateReady: !isLoadingGridState,
    enabled: !!ecoRfpId,
  });

  const toggleableRows = useMemo(
    () =>
      rowData
        .filter(
          (row) => row.rowType === "commission" || row.rowType === "subsidy",
        )
        .map((row) => ({ id: row.rowGroupId, label: row.coverageLabel })),
    [rowData],
  );

  const rowVisibility = useMemo(
    () => ({ rows: toggleableRows, hiddenRowGroupIds, toggleRowGroup }),
    [toggleableRows, hiddenRowGroupIds, toggleRowGroup],
  );

  const { showError } = useNotification();

  const updateCommission = useUpdateProposalCommission({
    rfpId: ecoRfpId,
    onError: (err) =>
      showError(extractErrorMessage(err, "Unable to save the commission.")),
  });
  const deleteCommission = useDeleteProposalCommission({
    rfpId: ecoRfpId,
    onError: (err) =>
      showError(extractErrorMessage(err, "Unable to clear the commission.")),
  });
  const showNotesError = (err: Error): void =>
    showError(extractErrorMessage(err, "Unable to save the notes."));
  const updateFreeText = useUpdateProposalFreeText({
    rfpId: ecoRfpId,
    onError: showNotesError,
  });
  const updateColumnFreeText = useUpdateCompsColumnFreeText({
    rfpId: ecoRfpId,
    onError: showNotesError,
  });

  const {
    getReview,
    upsertReview,
    clearReview,
    getBrokerHighlight,
    setBrokerHighlight,
    clearBrokerHighlight,
    getEdited,
    setEdited,
  } = useCompsCellReviews({
    rfpId: ecoRfpId,
    enabled: !!ecoRfpId,
    onSaveError: useCallback(
      (err: Error) => {
        showError(`Failed to save cell review: ${err.message}`);
      },
      [showError],
    ),
  });

  // Materialize-on-edit for unquoted cells, guarded against duplicate plans
  // during the request/refetch window (ADR-024).
  const { materialize: materializeCommission } = useMaterializeCompsCommission({
    rfpId: ecoRfpId,
    onError: useCallback(
      (err: Error) => {
        showError(`Unable to save the commission. ${err.message}`);
      },
      [showError],
    ),
  });

  const handleCommissionRowChange = useCallback(
    (
      event: CellValueChangedEvent<CompsGridRow>,
      carrierId: string,
      row: CompsGridRow,
    ) => {
      const cellKey = `${carrierId}:${row.rowGroupId}`;
      const proposalId = context.proposalIdByCellKey.get(cellKey);
      const proposalPlanId = context.proposalPlanIdByCellKey.get(cellKey);
      const cell = row[carrierId] as CarrierCommissionCell;

      const isCleared = cell.commissionType === null && cell.amount === null;
      if (isCleared) {
        if (proposalId && proposalPlanId) {
          deleteCommission.mutate({
            path: { id: ecoRfpId, proposalId, proposalPlanId },
          });
        }
        return;
      }

      const payload = buildCommissionPayload(cell);

      if (!payload.returnedType) {
        showError("Select a commission type before saving.");
        return;
      }

      if (proposalId && proposalPlanId) {
        updateCommission.mutate({ proposalId, proposalPlanId, payload });
        return;
      }

      const materializeTarget = context.materializeTargetByRowGroupId.get(
        row.rowGroupId,
      );
      if (materializeTarget) {
        materializeCommission({
          carrierId,
          carrierName: event.colDef.headerName,
          rfpBenefitPlanGroupId: materializeTarget.rfpBenefitPlanGroupId,
          planPosition: materializeTarget.planPosition,
          commission: payload,
        });
      }
    },
    [
      context,
      ecoRfpId,
      updateCommission,
      deleteCommission,
      materializeCommission,
      showError,
    ],
  );

  const handleFreeTextRowChange = useCallback(
    (carrierId: string) => {
      const values = collectFreeTextValues(
        rowData,
        carrierId,
        context.freeTextFieldByRowId,
      );
      const payload = {
        compsTechSubsidyText: values.compsTechSubsidyText ?? null,
        compsImplementationCreditText:
          values.compsImplementationCreditText ?? null,
        compsWellnessDollarsText: values.compsWellnessDollarsText ?? null,
        compsNotesText: values.compsNotesText ?? null,
      };

      if (carrierId === REQUESTED_COL_KEY || carrierId === INCUMBENT_COL_KEY) {
        updateColumnFreeText.mutate({
          columnKind: carrierId as CompsColumnKind,
          payload,
        });
        return;
      }

      const proposalId = context.proposalIdByCarrierId.get(carrierId);
      if (!proposalId) return;

      updateFreeText.mutate({ proposalId, payload });
    },
    [context, rowData, updateFreeText, updateColumnFreeText],
  );

  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<CompsGridRow>) => {
      if (!ecoRfpId) {
        logger.error(
          "[Comps] Cell edit fired without ecoRfpId — this should be unreachable",
        );
        showError("Unable to save your change. Please refresh and try again.");
        return;
      }
      const { data: row, colDef } = event;
      const carrierId = colDef.field;
      if (!row || !carrierId) return;

      if (row.rowType === "commission" && row.planGroupId) {
        handleCommissionRowChange(event, carrierId, row);
      } else if (row.rowType === "notes" || row.rowType === "subsidy") {
        handleFreeTextRowChange(carrierId);
      }
    },
    [ecoRfpId, handleCommissionRowChange, handleFreeTextRowChange, showError],
  );

  if (!rfpId) {
    return <Alert severity="error">Missing RFP id.</Alert>;
  }

  if (
    isLoadingDetails ||
    isLoadingComps ||
    isLoadingGridState ||
    isLoadingVisibility
  ) {
    return (
      <Box sx={{ width: "100%" }}>
        <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={48} />
      </Box>
    );
  }

  if (rfpDetailsError) {
    return (
      <Alert severity="error">
        {extractErrorMessage(rfpDetailsError, "Failed to load RFP details.")}
      </Alert>
    );
  }

  if (errorMessage) {
    return <Alert severity="error">{errorMessage}</Alert>;
  }

  return (
    <DialogProvider>
      <CellReviewProvider
        getReview={getReview}
        upsertReview={upsertReview}
        clearReview={clearReview}
        getBrokerHighlight={getBrokerHighlight}
        setBrokerHighlight={setBrokerHighlight}
        clearBrokerHighlight={clearBrokerHighlight}
        getEdited={getEdited}
        setEdited={setEdited}
      >
        <RowVisibilityProvider value={rowVisibility}>
          <AiFieldLookupProvider fieldLookup={aiFieldLookup}>
            <CompsGridContent
              rowData={rowData}
              hiddenRowGroupIds={hiddenRowGroupIds}
              colDefs={colDefs}
              gridStateProps={gridStateProps}
              context={context}
              onCellValueChanged={handleCellValueChanged}
            />
          </AiFieldLookupProvider>
        </RowVisibilityProvider>
        <CellReviewCommentDialogRenderer />
      </CellReviewProvider>
    </DialogProvider>
  );
};

export default Comps;
